import { inject, Injectable } from '@angular/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { LocalStorageID, LocalStorageStateService } from '@stylehaven/components';

/** One overview's facetgroep order: what is saved for it, and the two writes that change it. */
export interface FacetOrderForView {
    readonly order$: Observable<string[] | null>;
    save(ranking: string[]): void;
    reset(): void;
}

@Injectable({ providedIn: 'root' })
export class FacetOrderService {
    private readonly localStorageStateService: LocalStorageStateService = inject(LocalStorageStateService);
    private readonly facetOrderState$: BehaviorSubject<Record<string, string[]>> = new BehaviorSubject<
        Record<string, string[]>
    >({});

    constructor() {
        this.localStorageStateService.isInitialized$
            .pipe(filter(isInitialized => isInitialized))
            .subscribe(() => this.loadLocalStorageState());
    }

    /**
     * The saved ranking of one overview, or null when it has none. It stays live after a write, so a panel
     * binding to it sees its own reorder and its own reset without holding a second copy of the order.
     */
    getFacetOrder$(viewId: string): Observable<string[] | null> {
        return this.facetOrderState$.pipe(
            map(state => state[viewId] ?? null),
            distinctUntilChanged(isEqual)
        );
    }

    setFacetOrder(viewId: string, orderedKeys: string[]): void {
        this.updateState({ ...this.facetOrderState$.value, [viewId]: orderedKeys });
    }

    clearFacetOrder(viewId: string): void {
        const next: Record<string, string[]> = { ...this.facetOrderState$.value };
        delete next[viewId];

        this.updateState(next);
    }

    private updateState(state: Record<string, string[]>): void {
        this.facetOrderState$.next(state);
        this.localStorageStateService.setState(LocalStorageID.FacetOrder, state);
    }

    private loadLocalStorageState(): void {
        const storedState: Record<string, string[]> | null = this.localStorageStateService.getState(
            LocalStorageID.FacetOrder
        );

        if (storedState) {
            // What this session already changed is newer than what was on disk when it started.
            this.facetOrderState$.next({ ...storedState, ...this.facetOrderState$.value });
        }
    }
}

/** Binds the service to one overview, so a page names its id once instead of at every call. */
export const facetOrderForView = (service: FacetOrderService, viewId: string): FacetOrderForView => ({
    order$: service.getFacetOrder$(viewId),
    save: (ranking: string[]) => service.setFacetOrder(viewId, ranking),
    reset: () => service.clearFacetOrder(viewId)
});
